import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
export function HomePage() {
  usePageMeta('OnlineCollegeGuide | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. 100% free service.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  return <main className="flex-grow bg-light" data-id="element-1625" data-mp="5b8e96mhtvy5">
      <HeroSection onSelectionChange={fetchResults} data-id="element-1626" data-mp="3mqyauyp5exyn" />
      <TrustSignals data-id="element-1627" data-mp="3mo2cxadaw9a7" />

      {hasSearched ? <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1628" data-mp="3ro8af7694q07">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-1629" data-mp="j96l7qyhiq3m">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-1630" data-mp="o8156w86ukpb">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-1631" data-mp="a8iab54e15uy">
                  <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1632" data-mp="e9k7lgeqv7ua" />
                  <p className="text-gray-600 font-medium" data-id="element-1633" data-mp="2uyz7hr9ogsvz">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1634" data-mp="3f7qon1vzsxo5">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1635" data-mp="3aggxtxypjoao" />
                  <div data-id="element-1636" data-mp="35h4bdebr0j8m">
                    <p className="text-red-800 font-medium" data-id="element-1637" data-mp="35ifk7sk1wh6l">{apiError}</p>
                    <p className="text-red-600 text-sm mt-1" data-id="element-1638" data-mp="30jcbny4v3stv">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1639" data-mp="3o7m2jegxn6rv">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1640" data-mp="3k6k0kpvmsgm7" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-1641" data-mp="2csqfp584tz08" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1642" data-mp="28qves1u45nmz" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1643" data-mp="saoal7uhxkbb">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1644" data-mp="xa0txim5s995">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1645" data-mp="121amaqz3dqoq" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1646" data-mp="170dg1f9mgahe">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1647" data-mp="1bxly7fursein">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-1648" data-mp="3hsycm3jlokyk">
              <HomeInlineLeadCapture data-id="element-1649" data-mp="3htzoubccrdhv" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-1650" data-mp="txax8k39tgxt" />}
        </div> : <>
          <DegreeCategoryGrid data-id="element-1651" data-mp="12x678ll3b5w9" />
          <SEOContentSection data-id="element-1652" data-mp="12y8zoxy29gea" />
          <FAQSection data-id="element-1653" data-mp="12yhdcs20cpqj" />
        </>}
    </main>;
}